import { ChangeDetectionStrategy, Component, effect, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { SystemActionComponent } from "./abstract.system.action.component";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { WorkflowCustomAttributeMappingsComponent } from "./workflow.custom.attribute.mappings.component";
import {
    createEmptyWorkflowCustAtrbMappingRow,
    parseWorkflowCustAtrbMappings,
    serializeWorkflowCustAtrbMappings,
    WorkflowCustAtrbMappingUiRow,
} from "./workflow.cust.atrb.mappings.util";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { NameService } from "../../../services/name.service";
import { Item } from "../../../pojo/Item";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-edit-event-action",
    template: `
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventIdField']?.inputValue"
            (valueChange)="setInputValue('eventIdField', $event)"
            [label]="'Event ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Optional: map a field reference for event id when the form does not already carry a system event id (e.g., $CNET-100-EVENT_ID).'
            "
            [acceptedAnswerTypes]="acceptedFor('eventIdField')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventName']?.inputValue"
            (valueChange)="setInputValue('eventName', $event)"
            [label]="'Event Name'"
            [type]="'text'"
            [required]="false"
            [help]="'Use field variables like $CNET-123-EVENT_NAME'"
            [acceptedAnswerTypes]="acceptedFor('eventName')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['startDate']?.inputValue"
            (valueChange)="setInputValue('startDate', $event)"
            [label]="'Start Date'"
            [type]="'text'"
            [required]="false"
            [help]="'Required if no OBJECT_OCCURRENCE field on form. Use field variables like $CNET-123-START_DATE'"
            [acceptedAnswerTypes]="acceptedFor('startDate')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['endDate']?.inputValue"
            (valueChange)="setInputValue('endDate', $event)"
            [label]="'End Date'"
            [type]="'text'"
            [required]="false"
            [help]="'Required if no OBJECT_OCCURRENCE field on form. Use field variables like $CNET-123-END_DATE'"
            [acceptedAnswerTypes]="acceptedFor('endDate')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['primaryOrgId']?.inputValue"
            (valueChange)="setInputValue('primaryOrgId', $event)"
            [label]="'Primary Organization ID'"
            [type]="'text'"
            [required]="false"
            [help]="'Use field variables like $CNET-123-ORG_ID (optional)'"
            [acceptedAnswerTypes]="acceptedFor('primaryOrgId')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventTitle']?.inputValue"
            (valueChange)="setInputValue('eventTitle', $event)"
            [label]="'Event Title'"
            [type]="'text'"
            [required]="false"
            [help]="'Use field variables like $CNET-123-EVENT_TITLE'"
            [acceptedAnswerTypes]="acceptedFor('eventTitle')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventDescription']?.inputValue"
            (valueChange)="setInputValue('eventDescription', $event)"
            [label]="'Event Description'"
            [type]="'text'"
            [required]="false"
            [help]="'Maps a STRING field to the event description. Use $CNET-123-STRING.'"
            [acceptedAnswerTypes]="acceptedFor('eventDescription')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['internalNotes']?.inputValue"
            (valueChange)="setInputValue('internalNotes', $event)"
            [label]="'Internal Notes'"
            [type]="'text'"
            [required]="false"
            [help]="'Maps a STRING field to internal notes. Use $CNET-123-STRING.'"
            [acceptedAnswerTypes]="acceptedFor('internalNotes')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventComment']?.inputValue"
            (valueChange)="setInputValue('eventComment', $event)"
            [label]="'Comment'"
            [type]="'text'"
            [required]="false"
            [help]="'Maps a STRING field to a comment on the event profile. Use $CNET-123-STRING.'"
            [acceptedAnswerTypes]="acceptedFor('eventComment')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['confirmationText']?.inputValue"
            (valueChange)="setInputValue('confirmationText', $event)"
            [label]="'Confirmation Text'"
            [type]="'text'"
            [required]="false"
            [help]="'Maps a STRING field to confirmation text. Use $CNET-123-STRING.'"
            [acceptedAnswerTypes]="acceptedFor('confirmationText')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['expectedHeadcount']?.inputValue"
            (valueChange)="setInputValue('expectedHeadcount', $event)"
            [label]="'Expected Headcount'"
            [type]="'text'"
            [required]="false"
            [help]="'Maps an INTEGER field to expected headcount (set on event and segment). Use $CNET-123-INTEGER.'"
            [acceptedAnswerTypes]="acceptedFor('expectedHeadcount')"
        />

        <div class="system-input auto-collect-section">
            <small class="form-text auto-collect-info">
                Categories, event types, additional organizations, requirements, and contact roles are automatically
                collected from matching form field types. Optionally select additional static values below.
            </small>
        </div>

        @if (pickersLoading()) {
            <div class="system-input"><em>Loading...</em></div>
        } @else {
            <div class="system-input picker-section">
                <label class="picker-label">Additional Categories</label>
                <s25-ng-multiselect-search-criteria
                    [type]="'eventCategories'"
                    [selectedItems]="selectedCategories()"
                    (changed)="onCategoriesChange($event)"
                    [showResults]="true"
                />
            </div>
            <div class="system-input picker-section">
                <label class="picker-label">Additional Organizations</label>
                <s25-ng-multiselect-search-criteria
                    [type]="'organizations'"
                    [selectedItems]="selectedOrganizations()"
                    (changed)="onOrganizationsChange($event)"
                    [showResults]="true"
                />
            </div>
            <div class="system-input picker-section">
                <label class="picker-label">Additional Requirements</label>
                <s25-ng-multiselect-search-criteria
                    [type]="'eventRequirements'"
                    [selectedItems]="selectedRequirements()"
                    (changed)="onRequirementsChange($event)"
                    [showResults]="true"
                />
            </div>
        }

        <s25-ng-workflow-custom-attribute-mappings
            class="system-input"
            [itemTypeId]="itemIds.Event"
            [mappings]="customAttrMappings()"
            [title]="'Custom Attributes'"
            (mappingsChange)="onCustomAttrMappingsChange($event)"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['preEventDuration']?.inputValue"
            (valueChange)="setInputValue('preEventDuration', $event)"
            [label]="'Pre-Event Time'"
            [type]="'text'"
            [required]="false"
            [help]="'Map a DURATION form field reference for pre-event time.'"
            [acceptedAnswerTypes]="acceptedFor('preEventDuration')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['postEventDuration']?.inputValue"
            (valueChange)="setInputValue('postEventDuration', $event)"
            [label]="'Post-Event Time'"
            [type]="'text'"
            [required]="false"
            [help]="'Map a DURATION form field reference for post-event time.'"
            [acceptedAnswerTypes]="acceptedFor('postEventDuration')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['setupDuration']?.inputValue"
            (valueChange)="setInputValue('setupDuration', $event)"
            [label]="'Setup Time'"
            [type]="'text'"
            [required]="false"
            [help]="'Map a DURATION form field reference for setup time (before pre-event).'"
            [acceptedAnswerTypes]="acceptedFor('setupDuration')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['takedownDuration']?.inputValue"
            (valueChange)="setInputValue('takedownDuration', $event)"
            [label]="'Takedown Time'"
            [type]="'text'"
            [required]="false"
            [help]="'Map a DURATION form field reference for takedown time (after post-event).'"
            [acceptedAnswerTypes]="acceptedFor('takedownDuration')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['useAssignmentPolicy']?.inputValue || 'true'"
            (valueChange)="setInputValue('useAssignmentPolicy', $event)"
            [label]="'Use Assignment Policy'"
            [type]="'checkbox'"
            [required]="false"
            [help]="
                'When checked, applies assignment policy (reservations needing approval will be filtered out). When unchecked, skips assignment policy and saves all assignments.'
            "
            [acceptedAnswerTypes]="acceptedFor('useAssignmentPolicy')"
        />
    `,
    styles: `
        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }

        .auto-collect-info {
            color: #555;
            font-size: 13px;
            font-style: italic;
        }

        .picker-section {
            margin-bottom: 16px;
        }

        .picker-label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        WorkflowActionInputComponent,
        S25MultiselectModule,
        WorkflowCustomAttributeMappingsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditEventActionComponent extends SystemActionComponent {
    protected readonly actionCode = "EDIT_EVENT" as const;
    readonly itemIds = Item.Ids;
    readonly customAttrMappings = signal<WorkflowCustAtrbMappingUiRow[]>([]);
    readonly pickersLoading = signal<boolean>(true);
    readonly selectedCategories = signal<S25ItemI[]>([]);
    readonly selectedOrganizations = signal<S25ItemI[]>([]);
    readonly selectedRequirements = signal<S25ItemI[]>([]);
    private customAttrInitialized = false;
    private pickersInitialized = false;

    constructor() {
        super();
        effect(() => {
            const inputs = this.inputMap();
            if (!this.customAttrInitialized) {
                this.customAttrInitialized = true;
                this.initializeCustomAttributes(inputs);
            }
            if (!this.pickersInitialized) {
                this.pickersInitialized = true;
                this.initializePickers(inputs).finally(() => this.pickersLoading.set(false));
            }
        });
    }

    private async initializePickers(inputs: Record<string, { inputValue?: string } | undefined>) {
        const catIds = inputs["categoryIds"]?.inputValue;
        const orgIds = inputs["additionalOrgIds"]?.inputValue;
        const reqIds = inputs["requirementIds"]?.inputValue;

        if (catIds) {
            this.selectedCategories.set(await this.parseIdsAndFetchNames(catIds, "eventCategories"));
        }
        if (orgIds) {
            this.selectedOrganizations.set(await this.parseIdsAndFetchNames(orgIds, "organizations"));
        }
        if (reqIds) {
            this.selectedRequirements.set(await this.parseIdsAndFetchNames(reqIds, "eventRequirements"));
        }
    }

    private async parseIdsAndFetchNames(
        json: string,
        type: "eventCategories" | "organizations" | "eventRequirements",
    ): Promise<S25ItemI[]> {
        try {
            const ids: number[] = JSON.parse(json);
            if (!ids.length) return [];
            if (type === "organizations") {
                const nameResults = await NameService.getNames(
                    ids.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Organization })),
                );
                return ids.map((id) => ({
                    itemId: id,
                    itemName:
                        nameResults[NameService.itemKeyFromIds(id, Item.Ids.Organization)] || `Organization ${id}`,
                }));
            }
            const allItems =
                type === "eventCategories"
                    ? await SearchCriteriaService.getEventCategories()
                    : await SearchCriteriaService.getEventRequirements(false);
            const nameMap = new Map<number, string>(
                (allItems || []).map((item: any) => [item.itemId, String(item.itemName || "")]),
            );
            return ids.map((id) => ({ itemId: id, itemName: nameMap.get(id) || `${type} ${id}` }));
        } catch {
            return [];
        }
    }

    private initializeCustomAttributes(inputs: Record<string, { inputValue?: string } | undefined>) {
        let mappings = parseWorkflowCustAtrbMappings(inputs["customAttributes"]?.inputValue);
        if (!mappings.length) {
            mappings = [createEmptyWorkflowCustAtrbMappingRow()];
        }
        this.customAttrMappings.set(mappings);
    }

    onCustomAttrMappingsChange(rows: WorkflowCustAtrbMappingUiRow[]): void {
        this.customAttrMappings.set(rows);
        this.setInputValue("customAttributes", serializeWorkflowCustAtrbMappings(rows));
    }

    onCategoriesChange(items: S25ItemI[]) {
        this.selectedCategories.set(items);
        this.setInputValue("categoryIds", JSON.stringify(items.map((i) => i.itemId)));
    }

    onOrganizationsChange(items: S25ItemI[]) {
        this.selectedOrganizations.set(items);
        this.setInputValue("additionalOrgIds", JSON.stringify(items.map((i) => i.itemId)));
    }

    onRequirementsChange(items: S25ItemI[]) {
        this.selectedRequirements.set(items);
        this.setInputValue("requirementIds", JSON.stringify(items.map((i) => i.itemId)));
    }
}
